import { NextSeo } from 'next-seo';
import Breadcrumb from '@/components/breadcrumb';
import HtmlContentContainer from '@/components/html-content-container';
import LayoutBase from '@/components/layout-base';
import contentLayoutBaseData from '@/content/content-layout-base-data';
import type { CmsArticleDetail } from '@/lib/cms-article-detail';
import cmsArticleDetail from '@/lib/cms-article-detail';
import i18nMessagesLoad from '@/lib/i18n-messages-load';
import { localizationContextFromGetStaticPropsContext } from '@/lib/localization-contenxt';
import revalidateAfter from '@/lib/revalidate-after';
import type { NextGetStaticProps, NextPageWithLayout } from '../_app';
import type { GetStaticPaths } from 'next';

type StrankaPageUrlParams = { strankaSlug: string };

export type StrankaPageProps = {
  article: CmsArticleDetail<string>;
};

const StrankaPage: NextPageWithLayout<StrankaPageProps> = ({ article }) => {
  const { body, metaData, slug, teaserText, title } = article;

  const metaTagKeyWords = metaData.keyWords ? [{ content: metaData.keyWords, property: 'keywords' }] : [];

  return (
    <>
      <NextSeo
        additionalMetaTags={metaTagKeyWords}
        description={metaData.description || teaserText || ''}
        title={metaData.title || title}
      />
      <main className='mx-auto mb-8 max-w-7xl px-4 sm:px-6 lg:px-8'>
        <Breadcrumb className='py-4'>
          <Breadcrumb.Item href={`/stranky/${slug}`} isCurrenctPage={true} position={1}>
            {title}
          </Breadcrumb.Item>
        </Breadcrumb>

        <div className='mx-auto max-w-prose text-lg'>
          <h1 className='heading-page'>{title}</h1>
          <HtmlContentContainer
            applyDefaultClassName={false}
            as='section'
            className='prose prose-lg mx-auto mt-6'
            html={body}
          />
        </div>
      </main>
    </>
  );
};

StrankaPage.displayName = 'StrankaPage';

StrankaPage.getLayout = (page) => {
  return <LayoutBase>{page}</LayoutBase>;
};

export default StrankaPage;

// *****************************************
// *
// * getStaticProps
// *
// *****************************************

export const getStaticProps: NextGetStaticProps<StrankaPageProps, StrankaPageUrlParams> = async (
  getStaticPropsContext,
) => {
  if (getStaticPropsContext.params?.strankaSlug == null) return { notFound: true };
  const localizationContext = localizationContextFromGetStaticPropsContext(getStaticPropsContext);
  const intlMessages = await i18nMessagesLoad(localizationContext);
  const layoutData = await contentLayoutBaseData(localizationContext);
  const article = await cmsArticleDetail<string>(
    localizationContext,
    '/stranky',
    getStaticPropsContext.params.strankaSlug,
  );
  if (article == null) return { notFound: true, revalidate: revalidateAfter('1m') };

  return {
    props: {
      article,
      intlMessages,
      layoutData,
    },
    revalidate: revalidateAfter('1h'),
  };
};

// *****************************************
// *
// * getStaticProps
// *
// *****************************************

export const getStaticPaths: GetStaticPaths<StrankaPageUrlParams> = async ({ locales }) => {
  const articles: { locale: string; slug: string }[] = [];

  const overLocales = locales ?? ['cs'];
  for (const locale of overLocales) {
    const data = [
      // 'zakaznicky-servis',
      'technicka-podpora-vydejni-automaty',
      'kariera',
      'obchodni-podminky',
      'bezpecnostni-informace',
      'zasady-ochrany-osobnich-udaju',
      'zasady-pouzivani-souboru-cookies',
      'zasady-ochrany-osobnich-udaju-zamestnanci',
      'o-bunzl',
      'prace-ve-vyskach',
      'certifikaty',
      'velikostni-tabulky',
      'piktogramy',
      'udrzitelnost',
      'vydejni-automaty',
      'branding-odevu',
      'moznosti-prepravy',
    ];
    data.forEach((slug) => {
      articles.push({ locale, slug });
    });
  }

  return {
    fallback: 'blocking',
    paths: articles.map((a) => ({
      locale: locales == null ? undefined : a.locale,
      params: {
        strankaSlug: a.slug,
      },
    })),
  };
};
